import { AppFunctionComponent } from "../../types"
import { Options, RenderNode } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES, MARKS, Node } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import React, { ReactNode } from "react"
import {
  PrimaryHeading,
  QuaternaryHeading,
  QuinaryHeading,
  SecondaryHeading,
  SenaryHeading,
  TertiaryHeading,
} from "../../components/common/heading.component"
import {
  getEmbedFields,
  isAudio,
  isButton,
  isDownloadButton,
  mapEmbeddedEntryToAudio,
  mapEmbeddedEntryToButtonLink,
  mapEmbeddedEntryToDownloadButton,
  resolveIsEmail,
} from "../../contentful/options/rich-text"
import { RenderRichText } from "../../types"
import { RawLink } from "../../ui/components/link/link.component"
import { resolveLink } from "../../ui/components/link/link.helpers"
import { AudioComponentResolver } from "../audio/audio-component.resolver"
import { DownloadButtonResolver } from "../download-button/download-button.resolver"
import { getImage } from "gatsby-plugin-image"
import { Image } from "../../components/common/image.component"
import styled from "styled-components"
interface Props {
  content: RenderRichText
  options?: RenderNode
  children?: React.ReactNode
}

const AssetImage = styled(Image)`
  img {
    margin: auto;
  }
`

const getRenderMark = () => {
  return {
    [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text: ReactNode) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text: ReactNode) => <u>{text}</u>,
  }
}

const getHeadings = () => {
  return {
    [BLOCKS.HEADING_1]: (_: any, children: ReactNode) => (
      <PrimaryHeading renderAs={"h1"}>{children}</PrimaryHeading>
    ),
    [BLOCKS.HEADING_2]: (_: any, children: ReactNode) => (
      <SecondaryHeading renderAs={"h2"}>{children}</SecondaryHeading>
    ),
    [BLOCKS.HEADING_3]: (_: any, children: ReactNode) => (
      <TertiaryHeading renderAs={"h3"}>{children}</TertiaryHeading>
    ),
    [BLOCKS.HEADING_4]: (_: any, children: ReactNode) => (
      <QuaternaryHeading renderAs={"h4"}>{children}</QuaternaryHeading>
    ),
    [BLOCKS.HEADING_5]: (_: any, children: ReactNode) => (
      <QuinaryHeading renderAs={"h5"}>{children}</QuinaryHeading>
    ),
    [BLOCKS.HEADING_6]: (_: any, children: ReactNode) => (
      <SenaryHeading renderAs={"h6"}>{children}</SenaryHeading>
    ),
  }
}

const getHyperlinks = () => {
  return {
    [INLINES.ASSET_HYPERLINK]: (node: Node, children: ReactNode) => {
      const fields = getEmbedFields(node)
      return (
        <RawLink to={resolveLink(fields.file.url)} download>
          {children}
        </RawLink>
      )
    },
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      return <RawLink to={resolveIsEmail(node.data.uri)}>{children}</RawLink>
    },
  }
}

const getEmbedded = () => {
  return {
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      if (isButton(node)) {
        return mapEmbeddedEntryToButtonLink(node)
      }
      if (isDownloadButton(node)) {
        const { ...props } = mapEmbeddedEntryToDownloadButton(node)
        return <DownloadButtonResolver {...props} />
      }
      if (isAudio(node)) {
        const { ...props } = mapEmbeddedEntryToAudio(node)
        return <AudioComponentResolver {...props} />
      }
      /* if needed then add other types to map */
      return null
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
      /* TODO: wonder if better way exists */
      const fields = getEmbedFields(node)
      const image = getImage(fields)

      if (image && fields) {
        return <AssetImage image={image} alt={fields.file.title || ""} />
      }

      return null
    },
  }
}

const getInlineEmbedded = () => {
  return {
    [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
      const { internal } = node.data.target

      switch (internal?.type) {
        case "ContentfulButton":
          return mapEmbeddedEntryToButtonLink(node)
        default:
          return null
      }
    },
  }
}

const RichText: AppFunctionComponent<Props> = ({ content, options }) => {
  const richTextOptions: Options = {
    renderMark: getRenderMark(),
    renderNode: {
      ...getHeadings(),
      ...getHyperlinks(),
      ...getEmbedded(),
      ...getInlineEmbedded(),
      ...options,
      // add footnotes
    },
  }

  return <>{renderRichText(content, richTextOptions)}</>
}

export default RichText
